<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { uploadImage } from "@/lib/uploads";
import { fetchStampCard, addStampCard, updateStampCard } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref(""), uploading = ref(false);

const cardName = ref(""), cardImage = ref("");
const stampImages = ref([]);
const completion = ref({ number: null, image: "" });

const previewImage = ref("");
function openPreview(src) { if (src) previewImage.value = src; }

const newStamp = () => ({ qrcode: "", image: "", exclusiveGroup: "", level: null });

onMounted(async () => {
  if (!isEdit.value) { stampImages.value.push(newStamp()); return; }
  loading.value = true;
  try {
    const data = await fetchStampCard(id.value);
    if (!data) { notFound.value = true; return; }
    cardName.value = data.name || ""; cardImage.value = data.card || "";
    stampImages.value = Array.isArray(data.images) && data.images.length ? data.images.map(img => ({ qrcode: img.qrcode || "", image: img.image || "", exclusiveGroup: img.exclusiveGroup || "", level: img.level || null })) : [newStamp()];
    if (data.complete) completion.value = { number: data.complete.number ?? null, image: data.complete.image || "" };
  } catch { error.value = "Failed to load stamp card data."; }
  finally { loading.value = false; }
});

async function uploadInto(file, set) {
  if (!file) return;
  uploading.value = true; error.value = "";
  try { set(await uploadImage("stamp-cards", file)); }
  catch { error.value = "Couldn't upload the image."; }
  finally { uploading.value = false; }
}
const onCardImage = (e) => uploadInto(e.target.files?.[0], (u) => (cardImage.value = u));
const onCompletionImage = (e) => uploadInto(e.target.files?.[0], (u) => (completion.value.image = u));
const onStampImage = (e, i) => uploadInto(e.target.files?.[0], (u) => (stampImages.value[i].image = u));

async function submit() {
  success.value = false; error.value = ""; validationError.value = "";
  if (!cardName.value.trim()) { validationError.value = "Stamp Card name is required."; return; }
  if (stampImages.value.some(s => !s.qrcode)) { validationError.value = "All stamp images must have a QR Code label."; return; }
  const payload = { name: cardName.value, card: cardImage.value, images: stampImages.value.map(item => { const o = { qrcode: item.qrcode, image: item.image }; if (item.exclusiveGroup) o.exclusiveGroup = item.exclusiveGroup; if (item.level) o.level = Number(item.level); return o; }) };
  if (completion.value.number) payload.complete = { number: Number(completion.value.number), image: completion.value.image };
  saving.value = true;
  try {
    if (isEdit.value) await updateStampCard(id.value, payload); else await addStampCard(payload);
    success.value = true; setTimeout(() => router.push({ name: "stamps" }), 800);
  } catch { error.value = "Failed to save stamp card."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'stamps' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit" : "Add" }} Stamp Card</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Stamp card not found.</VAlert>
    <VCard v-else class="pa-4" max-width="900">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Stamp card saved successfully!</VAlert>
      <form @submit.prevent="submit">
        <VTextField v-model="cardName" label="Stamp Card Name" :disabled="isEdit" class="mb-4" />
        <div class="mb-4">
          <div class="text-body-2 font-weight-semibold mb-1">Main Card Image</div>
          <input type="file" accept="image/*" @change="onCardImage" />
          <img v-if="cardImage" :src="cardImage" alt="Card" style="max-height:120px; display:block; margin-top:8px; border-radius:6px" />
        </div>
        <div class="text-body-2 font-weight-semibold mb-2">Stamp Images</div>
        <VTable density="compact" class="mb-4">
          <thead><tr><th>QR Code Label</th><th>Image</th><th>Exclusive Group</th><th>Level (1–9)</th><th></th></tr></thead>
          <tbody>
            <tr v-for="(item, i) in stampImages" :key="i">
              <td><VTextField v-model="item.qrcode" density="compact" hide-details /></td>
              <td>
                <input type="file" accept="image/*" @change="(e) => onStampImage(e, i)" />
                <div v-if="item.image" class="d-flex align-center gap-1" style="margin-top:4px">
                  <img :src="item.image" style="max-height:48px; border-radius:4px" />
                  <VBtn icon size="x-small" variant="text" title="Enlarge image" @click="openPreview(item.image)">
                    <VIcon size="16">ri-zoom-in-line</VIcon>
                  </VBtn>
                </div>
              </td>
              <td><VTextField v-model="item.exclusiveGroup" density="compact" hide-details placeholder="Optional" /></td>
              <td><VTextField v-model.number="item.level" type="number" min="1" max="9" density="compact" hide-details style="max-width:80px" /></td>
              <td><VBtn v-if="stampImages.length > 1" icon="mdi:close" size="small" variant="text" color="error" @click="stampImages.splice(i,1)" /></td>
            </tr>
          </tbody>
        </VTable>
        <VBtn variant="tonal" size="small" class="mb-4" @click="stampImages.push(newStamp())">Add Stamp Image</VBtn>
        <VDivider class="mb-4" />
        <div class="text-body-2 font-weight-semibold mb-2">Completion Settings (optional)</div>
        <VRow class="mb-4">
          <VCol cols="12" md="4"><VTextField v-model="completion.number" label="Stamps to Complete" type="number" /></VCol>
          <VCol cols="12">
            <input type="file" accept="image/*" @change="onCompletionImage" />
            <img v-if="completion.image" :src="completion.image" style="max-height:80px; display:block; margin-top:8px; border-radius:6px" />
          </VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4">{{ validationError }}</VAlert>
        <div class="d-flex gap-2">
          <VBtn type="submit" :loading="saving" :disabled="uploading">{{ isEdit ? "Update" : "Create" }}</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'stamps' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>

    <VDialog :model-value="!!previewImage" max-width="640" @update:model-value="previewImage = ''">
      <VCard>
        <VCardText class="pa-2 d-flex justify-center">
          <img :src="previewImage" style="max-width:100%; max-height:80vh; border-radius:4px" />
        </VCardText>
        <VCardActions class="justify-end pa-2 pt-0">
          <VBtn variant="text" @click="previewImage = ''">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
